// Helpers for aggregating / colouring projects by their CRM sales pipeline
// stage (`crm_sales_pipelines`). The stage groups below mirror the seeded
// `group_name` values: Discovery → Quotation → Negotiation → Contract →
// Execution → Completed, plus the terminal Lost group.

export const PIPELINE_GROUP_COLORS = {
  Discovery:   '#8B5CF6',
  Quotation:   '#F59E0B',
  Negotiation: '#3B82F6',
  Contract:    '#34D399',
  Execution:   '#10B981',
  Completed:   '#047857',
  Lost:        '#EF4444',
};

export const PIPELINE_DEFAULT_COLOR = '#94A3B8';

// Stage-group display order, used when a pipeline carries no `position`.
const GROUP_ORDER = [
  'Discovery', 'Quotation', 'Negotiation', 'Contract', 'Execution', 'Completed', 'Lost',
];

export const UNASSIGNED_PIPELINE_LABEL = 'No pipeline';

// A deal is inactive once it lands in the Lost sales group.
export const isLostPipeline = (pipeline) =>
  (pipeline?.sales_group || pipeline?.group) === 'Lost';

// Lighten (percent > 0) / darken (percent < 0) a #rrggbb colour so several
// stages inside the same group stay visually distinct.
export const shadeColor = (hex, percent) => {
  const match = /^#?([\da-f]{6})$/i.exec(hex || '');
  if (!match) return hex;

  const num = parseInt(match[1], 16);
  const channels = [(num >> 16) & 255, (num >> 8) & 255, num & 255];
  const shifted = channels.map((c) => {
    const target = percent > 0 ? 255 : 0;
    const value = Math.round(c + (target - c) * Math.abs(percent));
    return Math.min(255, Math.max(0, value));
  });

  return `#${shifted.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
};

const pipelineKey = (pipeline) =>
  pipeline?.id != null ? `id:${pipeline.id}` : `name:${pipeline?.name || UNASSIGNED_PIPELINE_LABEL}`;

const sortValue = (entry) => {
  if (entry.position != null) return entry.position;
  const groupIndex = GROUP_ORDER.indexOf(entry.group);
  return (groupIndex === -1 ? GROUP_ORDER.length : groupIndex) * 100 + (entry.id || 0);
};

const toNumber = (v) => parseFloat(v) || 0;

/**
 * Aggregate projects by sales pipeline stage.
 * Returns entries sorted along the pipeline, each with a distinct colour
 * derived from its stage group.
 */
export const groupProjectsByPipeline = (projects = []) => {
  const byStage = projects.reduce((acc, project) => {
    const pipeline = project?.sales_pipeline;
    const key = pipeline ? pipelineKey(pipeline) : `name:${UNASSIGNED_PIPELINE_LABEL}`;
    const previous = acc[key];

    return {
      ...acc,
      [key]: {
        id:          pipeline?.id ?? null,
        name:        pipeline?.name || UNASSIGNED_PIPELINE_LABEL,
        group:       pipeline?.group || 'Other',
        salesGroup:  pipeline?.sales_group || pipeline?.group || 'Other',
        position:    pipeline?.position ?? null,
        count:       (previous?.count || 0) + 1,
        weight:      (previous?.weight || 0) + toNumber(project?.weight),
        amount:      (previous?.amount || 0) + toNumber(project?.amount),
      },
    };
  }, {});

  const ordered = Object.values(byStage).sort((a, b) => sortValue(a) - sortValue(b));

  // Shade stages sharing a group so adjacent slices remain distinguishable.
  const groupSizes = ordered.reduce(
    (acc, e) => ({ ...acc, [e.group]: (acc[e.group] || 0) + 1 }),
    {}
  );
  const seenPerGroup = {};

  return ordered.map((entry) => {
    const index = seenPerGroup[entry.group] || 0;
    seenPerGroup[entry.group] = index + 1;

    const base = PIPELINE_GROUP_COLORS[entry.group] || PIPELINE_DEFAULT_COLOR;
    const size = groupSizes[entry.group];
    const spread = size > 1 ? (index - (size - 1) / 2) * (0.5 / size) : 0;

    return { ...entry, color: spread === 0 ? base : shadeColor(base, spread) };
  });
};

export const countActiveProjects = (projects = []) =>
  projects.filter((p) => !isLostPipeline(p?.sales_pipeline)).length;
